"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  createPersonalNote,
  getPersonalNotes,
  type PersonalNoteRow,
} from "@/lib/api";
// [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001]
import {
  FLOATING_SETTINGS_EVENT,
  type FloatingPosition,
  floatingPositionStyle,
  loadFloatingPosition,
  loadFloatingVisible,
  resetFloatingPosition,
  saveFloatingPosition,
} from "@/lib/floatingPosition";
import FloatingPositionMenu from "@/components/FloatingPositionMenu";

const WIDGET_OPEN_KEY = "quick_note_widget_open";

function hasToken(): boolean {
  if (typeof window === "undefined") return false;
  return !!localStorage.getItem("access_token");
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return "";
  // ISO datetime → MM/DD HH:mm
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd} ${hh}:${mi}`;
  } catch {
    return "";
  }
}

/**
 * 우하단 플로팅 빠른 메모 위젯.
 * - 채팅 위젯(bottom-5 right-5) 위쪽 (bottom-24 right-5) 에 위치
 * - 클릭 → 360×440 패널 열림
 * - 빠른 메모 작성 + 최근 5건 표시 (클릭 시 /notes?noteId={id})
 * - 인증된 사용자에게만 노출
 */
export default function QuickNoteWidget() {
  const router = useRouter();
  const pathname = usePathname();

  const [authed, setAuthed] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  // [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001] 기기별 위치(localStorage).
  const [position, setPosition] = useState<FloatingPosition>("bottom-right");
  // [ERP-FLOATING-CHAT-MEMO-VISIBILITY-PER-DEVICE-DEV-20260629-001] 기기별 표시 여부.
  const [visible, setVisible] = useState<boolean>(true);

  const [recent, setRecent] = useState<PersonalNoteRow[]>([]);
  const [loadingRecent, setLoadingRecent] = useState<boolean>(false);

  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const contentRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setMounted(true);
    if (typeof window === "undefined") return;
    setAuthed(hasToken());
    const raw = localStorage.getItem(WIDGET_OPEN_KEY);
    if (raw === "1") setOpen(true);
    // [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001]
    setPosition(loadFloatingPosition("memo"));
    // [ERP-FLOATING-CHAT-MEMO-VISIBILITY-PER-DEVICE-DEV-20260629-001]
    // 표시여부 로드 + 전역 설정버튼/다른 탭 변경 동기화.
    setVisible(loadFloatingVisible("memo"));
    const onSettings = () => {
      setVisible(loadFloatingVisible("memo"));
      setPosition(loadFloatingPosition("memo"));
    };
    window.addEventListener(FLOATING_SETTINGS_EVENT, onSettings);
    window.addEventListener("storage", onSettings);
    return () => {
      window.removeEventListener(FLOATING_SETTINGS_EVENT, onSettings);
      window.removeEventListener("storage", onSettings);
    };
  }, []);

  // [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001] 위치 변경/초기화 핸들러.
  const handleSelectPosition = useCallback((pos: FloatingPosition) => {
    setPosition(pos);
    saveFloatingPosition("memo", pos);
  }, []);
  const handleResetPosition = useCallback(() => {
    resetFloatingPosition("memo");
    setPosition(loadFloatingPosition("memo"));
  }, []);

  const posStyle = floatingPositionStyle("memo", position);

  // 라우트 변할 때 인증 상태 재확인 (로그인/로그아웃 반영)
  useEffect(() => {
    if (typeof window === "undefined") return;
    setAuthed(hasToken());
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(WIDGET_OPEN_KEY, open ? "1" : "0");
  }, [open]);

  const refreshRecent = useCallback(async () => {
    if (!authed) return;
    setLoadingRecent(true);
    try {
      const res = await getPersonalNotes({
        is_archived: false,
        limit: 5,
        offset: 0,
      });
      setRecent(res.items);
    } catch (e: any) {
      // 조용히 실패 — 위젯은 보조 도구
      console.warn("[QuickNote] recent fetch failed", e?.message ?? e);
    } finally {
      setLoadingRecent(false);
    }
  }, [authed]);

  useEffect(() => {
    if (open && authed) {
      refreshRecent();
    }
  }, [open, authed, refreshRecent]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!content.trim() && !title.trim()) {
      setError("내용을 입력해주세요.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createPersonalNote({
        title: title.trim() || undefined,
        content: content,
      });
      setTitle("");
      setContent("");
      contentRef.current?.focus();
      await refreshRecent();
    } catch (e: any) {
      setError(e?.message ?? "저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  }, [saving, title, content, refreshRecent]);

  const handleOpenNote = useCallback(
    (id: number) => {
      setOpen(false);
      router.push(`/notes?noteId=${id}`);
    },
    [router],
  );

  const handleGoNotes = useCallback(() => {
    setOpen(false);
    router.push("/notes");
  }, [router]);

  // /notes 페이지에서는 위젯 자체를 숨겨 작업 영역과의 중복을 방지
  const isOnNotesPage =
    pathname === "/notes" || pathname?.startsWith("/notes/");
  // [ERP-MOBILE-20260513-002] 모바일 ERP 에서는 우측 하단 플로팅 메모 버튼 숨김.
  const isMobile = pathname === "/mobile" || pathname?.startsWith("/mobile/");
  // [ERP-POS-FLOATING-WIDGET-HIDE-20260522-001] 오프라인 매장 POS 화면에서는 플로팅 메모 버튼 숨김.
  const isPos = pathname === "/store" || pathname?.startsWith("/store/");
  // [PURCHASE-003 Phase 1] iPad 전용 화면은 하단 고정 버튼과 겹치므로 플로팅 메모 버튼 숨김.
  const isTablet = pathname === "/tablet" || pathname?.startsWith("/tablet/");

  // [ERP-FLOATING-CHAT-MEMO-VISIBILITY-PER-DEVICE-DEV-20260629-001] 숨김 설정 시 미렌더(설정버튼으로 복구).
  if (!mounted || !authed || isOnNotesPage || isMobile || isPos || isTablet || !visible) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          aria-label="빠른 메모 열기"
          onClick={() => setOpen(true)}
          style={posStyle}
          className="fixed z-[150] w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg flex items-center justify-center text-2xl"
        >
          📝
        </button>
      )}

      {open && (
        <div
          style={posStyle}
          className="fixed z-[150] w-[360px] max-w-[calc(100vw-1.5rem)] h-[440px] max-h-[calc(100vh-2rem)] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col border border-gray-200"
          role="dialog"
          aria-label="빠른 메모"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b bg-amber-50">
            <div className="flex items-center gap-2">
              <span className="text-base">📝</span>
              <span className="text-sm font-semibold text-gray-800">
                빠른 메모
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001] 위치 변경 메뉴 */}
              <FloatingPositionMenu
                current={position}
                onSelect={handleSelectPosition}
                onReset={handleResetPosition}
                tone="amber"
              />
              <button
                type="button"
                onClick={handleGoNotes}
                className="text-[11px] text-blue-600 hover:underline px-1.5 py-0.5"
              >
                전체 보기
              </button>
              <button
                type="button"
                aria-label="닫기"
                onClick={() => setOpen(false)}
                className="text-gray-500 hover:text-gray-800 px-1.5 py-0.5"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="px-3 py-2 border-b">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="제목 (선택)"
              maxLength={200}
              className="w-full text-sm border border-gray-200 rounded px-2 py-1 mb-2 focus:outline-none focus:border-amber-400"
            />
            <textarea
              ref={contentRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={
                "메모 내용을 입력하세요. (@주문번호 로 주문에 연결할 수 있습니다)"
              }
              rows={4}
              className="w-full text-sm border border-gray-200 rounded px-2 py-1 resize-none focus:outline-none focus:border-amber-400"
            />
            {error && (
              <div className="text-[11px] text-red-600 mt-1">{error}</div>
            )}
            <div className="flex items-center justify-between mt-2">
              <div className="text-[11px] text-gray-400">
                {content.length.toLocaleString()} / 100,000
              </div>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="text-xs px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white"
              >
                {saving ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-2">
            <div className="text-[11px] font-semibold text-gray-500 mb-1">
              최근 메모
            </div>
            {loadingRecent ? (
              <div className="text-xs text-gray-400 py-3 text-center">
                불러오는 중...
              </div>
            ) : recent.length === 0 ? (
              <div className="text-xs text-gray-400 py-3 text-center">
                아직 메모가 없습니다.
              </div>
            ) : (
              <ul className="space-y-1">
                {recent.map((n) => {
                  const preview = (n.content || "").slice(0, 60);
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => handleOpenNote(n.id)}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 border border-transparent hover:border-gray-200"
                      >
                        <div className="flex items-center gap-1.5">
                          {n.is_pinned && (
                            <span className="text-[10px] text-amber-600">
                              📌
                            </span>
                          )}
                          <div className="text-xs font-medium text-gray-800 truncate flex-1">
                            {n.title || "제목 없음"}
                          </div>
                          <div className="text-[10px] text-gray-400 shrink-0">
                            {fmtDate(n.updated_at)}
                          </div>
                        </div>
                        {preview && (
                          <div className="text-[11px] text-gray-500 truncate mt-0.5">
                            {preview}
                          </div>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
